import React, { useState, useEffect, useCallback } from 'react';
import produce from 'immer';
import { Stage, Layer, Rect, Text, Group, Circle, Shape } from 'react-konva';
import Konva from 'konva';

import PdfPreview from 'components/Shared/PdfPreview'
import { useEditorState, useEditorActions } from '../../editor-context'
import { useTranslation } from 'react-i18next';

const PDF_VIEWER_WIDTH = 720;

export function PdfFormat() {
    const { document: doc, currentPage, isEditable } = useEditorState()
    const { setTotalPages, updateSignature, removeSignature, setCurrentPage } = useEditorActions()
    const [height, setHeight] = useState()
    const [width, setWidth] = useState(0);
    const [rotate, setRotate] = useState(0);
    const protectedStatus = ['private', '', 'ACCEPTED_NEGOTIATION'];
   
    const handleDocumentLoad = ({ numPages }) => {
        setTotalPages(numPages)
    }
    const handlePageLoad = ({  width, height, ...page }) => {
        setHeight(height)
        setWidth(width)

    }
    useEffect(() => {
        setWidth(0)
    }, [currentPage])

    const handleRemoveSignature = (participant, signature) => {
        removeSignature({ participantId: participant._id, signatureId: signature._id })
    }

    const handleUpdateSignature = (participantId, signature) => {
        updateSignature({ participantId, signature })
    }
    
    return (
        <div className="editor-canvas-pdf" style={{width}}>
            <PdfPreview
                width={width}
                url={doc.importedPdf}
                pageNumber={currentPage}
                resetWidthOnUrlChange
                onPageChange={setCurrentPage}
                onDocumentLoad={handleDocumentLoad}
                onPageLoad={handlePageLoad}
                clickPanels
            >    
                    <PDFSignaturesContainer
                        resetWidthOnUrlChange
                        document={doc}
                        width={width} 
                        height={height}
                        rotate={rotate}
                        currentPage={currentPage}
                        isEditable={protectedStatus.includes(doc.status)}
                        onRemoveSignature={handleRemoveSignature}
                        onUpdateSignature={handleUpdateSignature}
                    />
            </PdfPreview>
        </div>
    )
}

export function PDFSignaturesContainer({ document: doc, width, height, rotate = 0, currentPage, isEditable, showSignatureConfirmations, participantId, signatureConfirmations = {}, ratio = 1, onUpdateSignature, onRemoveSignature, onConfirmSignature }) {
   
    const [participants, setParticipants] = useState(doc.participants);

    useEffect(() => {
        setParticipants(doc.participants ?? [])
    }, [doc.participants])

    const handleDragStart = useCallback((e) => {
        e.target.setAttrs({
          shadowOffset: {
            x: 15,
            y: 15
          },
          scaleX: 1,
          scaleY: 1
        });
      }, []);
     
      const handleDragEnd = e => {
        const participantId = e.target.attrs.participantId;
        const signature = {
          _id: e.target.attrs.id,
          left: e.target.attrs.x,
          top: e.target.attrs.y,
          width: 200,
          height: 100,
          page: currentPage
        }
  
        setParticipants(produce(draft => {
            const participantIdx = draft.findIndex(p => p._id === participantId);
            const participant = draft[participantIdx];
            
            const signatureIdx = participant.signatures.findIndex(s => s._id === signature._id);

            participant.signatures[signatureIdx] = signature;

        }))

        onUpdateSignature(participantId, signature)

        e.target.to({
          duration: 0.5,
          easing: Konva.Easings.ElasticEaseOut,
          scaleX: 1,
          scaleY: 1,
          shadowOffsetX: 5,
          shadowOffsetY: 5
        });
      };
  
      const handleDragBound = (signature, pos, event) => {
        const newY = pos.y < 2 ? 2 : (pos.y > height - signature.height) ? height - signature.height: pos.y;
        const newX = pos.x < 2 ? 2 : (pos.x > width - signature.width) ? width - signature.width: pos.x;
        
        return {
          x: newX,
          y: newY
        };
      }
  
    
    return (
        <div style={{position: 'absolute', top: 0, left:0, width, height}}>
            <Stage width={width} height={height} className="layer" id='layer'>
            <Layer>                  
                {
                    participants.map((participant) => {

                        if (!participant.signatures) return null;

                        return participant.signatures.map((signature, i) => { 
                            
                            if (signature.page !== currentPage) return null;

                            return (
                                    <SignatureElement 
                                        key={signature._id}
                                        participant={participant} 
                                        signature={signature} 
                                        hideRemove={!isEditable}
                                        ratio={ratio}
                                        signatureType={doc.signatureType}
                                        showSignatureConfirmations={showSignatureConfirmations && participant._id === participantId}
                                        isConfirmed={signatureConfirmations[signature._id]}
                                        onRemove={onRemoveSignature}
                                        onConfirmSignature={onConfirmSignature}
                                        draggable={isEditable} 
                                        onDragStart={handleDragStart}
                                        onDragEnd={handleDragEnd}
                                        onDragBound={handleDragBound}
                                />
                            )
                        })
                    })
                }
                </Layer>
            </Stage>
        </div>
    );
}

const SIGNATURE_DEFAULT_WIDTH = 200;
const SIGNATURE_DEFAULT_HEIGHT = 100;

function SignatureElement({ participant, signature, hideRemove, draggable, showSignatureConfirmations, isConfirmed, signatureType, ratio = 1, onDragStart, onDragEnd, onDragBound, onRemove, onConfirmSignature }) {
    const { width = SIGNATURE_DEFAULT_WIDTH, height = SIGNATURE_DEFAULT_HEIGHT, customSize } = signature;
    const realWidth = customSize ? width*ratio : SIGNATURE_DEFAULT_WIDTH*ratio;
    const realHeight = customSize ? height*ratio : SIGNATURE_DEFAULT_HEIGHT*ratio;

    const { t } = useTranslation();

    const handleRemove = () => {
        onRemove(participant, signature)
    }
    const handleConfirmSignature = () => {
        if (showSignatureConfirmations) {
            onConfirmSignature(participant, signature)
        }
    }

    const handleDragBound = (pos, event) => {
        return onDragBound({
            ...signature,
            width,
            height
        }, pos, event);

        
    }

    return (
        <Group   
            key={signature._id}
            x={signature.left * ratio} 
            y={signature.top * ratio}
            id={signature._id}
            width={realWidth}
            height={realHeight}
            participantId={participant._id}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            dragBoundFunc={handleDragBound}
            onClick={handleConfirmSignature}
        >
           
            <Text 
                fontSize={18*ratio} 
                offsetY={-realHeight/2.5}
                width={realWidth}
                height={realHeight}
                text={`${t('common.signature_area')} \n ${participant.label}`}
                align="center"
            />
            <Rect
                width={realWidth}
                height={realHeight}
                fill="black"
                opacity={0.3}
                shadowEnabled={false}
            />
            {
                showSignatureConfirmations &&
                <Group   
                    x={5}
                    y={5}
                    onMouseDown={handleConfirmSignature}
                >
                <Shape
                    onClick={handleConfirmSignature}
                    sceneFunc={function (ctx, shape) {
                        const centerX = 0;
                        const centerY = 0;
                        ctx.beginPath()

                         //circle  
                        const radius = 9*ratio;
                        //draw circle
                        ctx.beginPath();
                        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
                        ctx.fillStyle = isConfirmed ? 'green' : 'orange';
                        ctx.fill();
                        ctx.lineWidth = 1;
                        ctx.strokeStyle = isConfirmed ? 'green' : 'orange';
                        ctx.stroke();
                        ctx.fillStrokeShape(this);
                        
                        //draw tick
                        if (isConfirmed) {
                            ctx.beginPath();
                            ctx.moveTo(-4*ratio,-1*ratio);
                            ctx.lineTo(-1*ratio, 2*ratio);
                            ctx.lineTo(5*ratio,-4*ratio);
                            ctx.lineWidth = 2*ratio;
                            ctx.strokeStyle = '#fff';
                            ctx.stroke();    
                        }
                    }}
                    // stroke="white"
                    strokeWidth={2}
                />
                </Group>
            }
                
             {!hideRemove && <Group   
                    x={realWidth}
                    onClick={handleRemove}
                >
                    <Circle 
                        participantId={participant._id}
                       
                        signatureId={signature._id}
                        
                        fill="red"
                        radius={9}
                    />
                    <Shape
                        sceneFunc={(ctx, shape) => {
                            const x = 0;
                            const y = 0;
                            ctx.beginPath();
                            const width = 4;

                            ctx.moveTo(x - width, y - width);
                            ctx.lineTo(x + width, y + width);

                            ctx.moveTo(x + width, y - width);
                            ctx.lineTo(x - width, y + width);
                            ctx.stroke();
                            ctx.fillStrokeShape(shape)
                        }}
                        stroke="white"
                        strokeWidth={2}
                    />
                </Group>}
           
        </Group>
        
    )
}
